import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

import { uploadProgressStore } from '../store/UploadProgressStore'
import { UploadItem } from '../types'
import { apiPrefix } from '../utils/apiUtils'
import { generateUploadId, getFileType } from '../utils/fileUtils'
import { extractFileFromFormData, extractFormDataParams, getFileKeyFromFormData } from '../utils/formDataUtils'

let isInterceptorSetup = false

export const setupUploadInterceptor = () => {
  if (isInterceptorSetup) {
    return
  }

  const requestInterceptor = axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (config.data instanceof FormData) {
        const metadata = (config as any).metadata || {}
        if (metadata.uploadId) {
          return config
        }

        const targetEndpoint = uploadProgressStore.getTargetEndpoint()
        const currentUrl = config.url ? apiPrefix(config.url) : null

        if (targetEndpoint && currentUrl !== targetEndpoint) {
          return config
        }

        const file = extractFileFromFormData(config.data)
        if (file) {
          const uploadId = generateUploadId()
          const cancelToken = axios.CancelToken.source()

          if (!targetEndpoint && currentUrl) {
            const formDataParams = extractFormDataParams(config.data)
            const fileKey = getFileKeyFromFormData(config.data) || 'file'
            uploadProgressStore.setFormDataParams(formDataParams, fileKey)
          }

          config.cancelToken = cancelToken.token
          ;(config as any).metadata = { ...metadata, uploadId }

          const progressHandler = (progressEvent: any) => {
            if (progressEvent.total && progressEvent.total > 0) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              uploadProgressStore.updateUploadProgress(uploadId, Math.max(0, Math.min(100, progress)))
            } else if (progressEvent.loaded > 0) {
              uploadProgressStore.updateUploadProgress(uploadId, 1)
            } else {
              uploadProgressStore.updateUploadProgress(uploadId, 0)
            }
          }

          config.onUploadProgress = progressHandler

          const uploadItem: UploadItem = {
            id: uploadId,
            file,
            fileName: file.name,
            fileType: getFileType(file.name),
            status: 'uploading',
            progress: 0,
            displayProgress: 0,
            cancelToken,
            config: {
              ...config,
              url: currentUrl || config.url
            } as AxiosRequestConfig,
            createdAt: new Date()
          }

          uploadProgressStore.registerUpload(uploadItem)
        }
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  const responseInterceptor = axios.interceptors.response.use(
    (response: AxiosResponse) => {
      const uploadId = (response.config as any).metadata?.uploadId
      if (uploadId) {
        const uploadedTo = response.data?.uploadedTo || response.data?.folder || response.data?.path
        uploadProgressStore.completeUpload(uploadId, uploadedTo)
      }
      return response
    },
    (error: AxiosError) => {
      const uploadId = (error.config as any)?.metadata?.uploadId
      if (uploadId && !axios.isCancel(error)) {
        const errorMessage = (error.response?.data as any)?.message || error.message || 'Upload failed'
        uploadProgressStore.failUpload(uploadId, errorMessage)
      }
      return Promise.reject(error)
    }
  )

  isInterceptorSetup = true

  return () => {
    axios.interceptors.request.eject(requestInterceptor)
    axios.interceptors.response.eject(responseInterceptor)
    isInterceptorSetup = false
  }
}
